import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomModal from '../../../Modal';
import FormGroup from '../../../FormGroup';
import Loading from '../../../Loading';
import Timer from '../../../Timer';

const activationTimersCache = new Map();
const activationWarningOverrides = new Set();

const isValidId = (id) => id !== undefined && id !== null;
const hasWarningOverride = (id) => isValidId(id) && activationWarningOverrides.has(id);

const getRemainingTime = (id) => {
    if (!isValidId(id)) {
        return 0;
    }
    const deadline = activationTimersCache.get(id);
    if (!deadline) {
        return 0;
    }
    const diff = Math.ceil((deadline - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
};

const persistTimer = (id, seconds) => {
    if (!isValidId(id)) {
        return;
    }
    if (seconds > 0) {
        activationTimersCache.set(id, Date.now() + seconds * 1000);
    } else {
        activationTimersCache.delete(id);
    }
};

export const PaymentMethodActivation = ({
    id: propId,
    timeToRenew: propTimeToRenew,
    methodPaymentId: propMethodPaymentId,
    email: propEmail,
    loadingActivationCode: propLoadingActivationCode,
    searchMehod: propSearchMehod,
    activationPaymentDetails: propActivationPaymentDetails,
    submitLinkConfirm: propSubmitLinkConfirm,
    onCancel: propOnCancel,
    t
}) => {
    const initialOverride = hasWarningOverride(propId);
    const initialRemainingTime = initialOverride ? 0 : getRemainingTime(propId);
    const [loading, setLoading] = useState(!initialOverride && initialRemainingTime === 0);
    const [loadingActivation, setLoadingActivation] = useState(false);
    const [view, setView] = useState(initialOverride ? 'warning' : (initialRemainingTime > 0 ? 'code' : 'warning'));
    const [codeRequested, setCodeRequested] = useState(!initialOverride && initialRemainingTime > 0);
    const [code, setCode] = useState('');
    const [timeToRenew, setTimeToRenew] = useState(initialRemainingTime);
    const [errorsCode, setErrorsCode] = useState([]);

    useEffect(() => {
        if (!isValidId(propId)) {
            return;
        }

        if (!hasWarningOverride(propId) && getRemainingTime(propId) === 0) {
            setLoading(true);
        }
        propSearchMehod(propId, () => {
            setLoading(false);
        });
    }, [propId, propSearchMehod]);

    useEffect(() => {
        if (!isValidId(propId)) {
            return;
        }

        const isOverride = hasWarningOverride(propId);
        const cachedTime = isOverride ? 0 : getRemainingTime(propId);
        setTimeToRenew(cachedTime);
        setCodeRequested(!isOverride && cachedTime > 0);
        setView(isOverride ? 'warning' : (cachedTime > 0 ? 'code' : 'warning'));
        setCode('');
        setErrorsCode([]);
    }, [propId]);

    useEffect(() => {
        if (!isValidId(propId)) {
            return;
        }

        if (propMethodPaymentId !== propId) {
            return;
        }

        if (hasWarningOverride(propId)) {
            persistTimer(propId, 0);
            return;
        }

        if (typeof propTimeToRenew !== 'number') {
            return;
        }

        if (propTimeToRenew > 0) {
            persistTimer(propId, propTimeToRenew);
            const remaining = getRemainingTime(propId);
            setTimeToRenew(remaining);
            setCodeRequested(true);
            setView('code');
        } else if (propTimeToRenew === 0) {
            persistTimer(propId, 0);
            setTimeToRenew(0);
            if (!codeRequested) {
                setView('warning');
            }
        }
    }, [propId, propMethodPaymentId, propTimeToRenew, codeRequested]);

    useEffect(() => {
        if (
            propLoadingActivationCode !== undefined &&
            !propLoadingActivationCode &&
            view !== 'code' &&
            (codeRequested || timeToRenew > 0)
        ) {
            setView('code');
        }
    }, [propLoadingActivationCode, view, codeRequested, timeToRenew]);

    const onSuccessActivation = useCallback(() => {
        setLoadingActivation(false);
        persistTimer(propId, 0);
        if (isValidId(propId)) {
            activationWarningOverrides.add(propId);
        }
        setCodeRequested(false);
        setTimeToRenew(0);
        setView('warning');
        propOnCancel();
    }, [propId, propOnCancel]);

    const onErrorActivation = useCallback((error) => {
        setLoadingActivation(false);
        setErrorsCode(error);
    }, []);

    const onActivationPaymentDetail = useCallback(() => {
        setLoadingActivation(true);
        propActivationPaymentDetails(propId, code, onSuccessActivation, onErrorActivation);
    }, [propId, code, propActivationPaymentDetails, onSuccessActivation, onErrorActivation]);

    const onSendActivationCode = useCallback(() => {
        if (hasWarningOverride(propId)) {
            activationWarningOverrides.delete(propId);
        }
        setCodeRequested(true);
        propSubmitLinkConfirm(propId);
    }, [propId, propSubmitLinkConfirm]);

    const onChangeCode = useCallback((event) => {
        setCode(event.target.value);
    }, []);

    const onEndTimer = useCallback(() => {
        persistTimer(propId, 0);
        setTimeToRenew(0);
    }, [propId]);

    const renderTimeOrSend = useCallback(() => {
        const methodActivation = t?.paymentPage?.methodActivation || {};
        const isDisable = timeToRenew > 0;

        return (
            <div className="big-field">
                {isDisable
                    ? (
                        <React.Fragment>
                            {`${methodActivation.timer} `}
                            <Timer time={timeToRenew} onStop={onEndTimer} />
                        </React.Fragment>
                    )
                    : <a href="#!" onClick={onSendActivationCode}>{methodActivation.repetSend}</a>
                }
            </div>
        )
    }, [t, timeToRenew, onEndTimer, onSendActivationCode]);

    const renderSendActivationCode = useCallback(() => {
        const methodActivation = t?.paymentPage?.methodActivation || {};

        return (
            <p>{methodActivation.text} <strong>{propEmail}</strong>?</p>
        )
    }, [t, propEmail]);

    const renderSendCode = useCallback(() => {
        const methodActivation = t?.paymentPage?.methodActivation || {};

        return (
            <div className="form-horizontal activation">
                <FormGroup>
                    {methodActivation.textAfter}
                </FormGroup>
                <FormGroup
                    fieldClass="big-field"
                    label={methodActivation.labelToCode}
                    errors={errorsCode}
                    button={renderTimeOrSend()}
                >
                    <input
                        className="form-control"
                        value={code}
                        onChange={onChangeCode}
                    />
                </FormGroup>
            </div>
        );
    }, [t, errorsCode, renderTimeOrSend, code, onChangeCode]);

    const methodActivation = t?.paymentPage?.methodActivation || {};
    const actions = t?.actions || {};
    const modalLoading = propLoadingActivationCode || loadingActivation;
    const isWarning = view === 'warning';
    const onConfirm = isWarning ? onSendActivationCode : onActivationPaymentDetail;

    return (
        <CustomModal
            visible={true}
            title={methodActivation.title}
            confirmName={isWarning ? actions.send : actions.activate}
            onClose={propOnCancel}
            onConfirm={onConfirm}
            bsSize={'sm'}
            loading={modalLoading}
        >
            {loading
                ? <Loading center={true} />
                : isWarning
                    ? renderSendActivationCode()
                    : renderSendCode()
            }
        </CustomModal>
    );
};

PaymentMethodActivation.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    timeToRenew: PropTypes.number,
    methodPaymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    loadingActivationCode: PropTypes.bool,
    searchMehod: PropTypes.func,
    activationPaymentDetails: PropTypes.func,
    submitLinkConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    t: PropTypes.object
};

PaymentMethodActivation.defaultProps = {
    id: null,
    timeToRenew: 0,
    methodPaymentId: null,
    email: '',
    loadingActivationCode: false,
    searchMehod: () => {},
    activationPaymentDetails: () => {},
    submitLinkConfirm: () => {},
    onCancel: () => {}
};

export default withTranslation(PaymentMethodActivation);
